import {
  CREDIT_RATES,
  EMPTY_STYLE_RULES,
  MAX_CUSTOM_TONES,
  MAX_TONE_DESCRIPTION_LENGTH,
  MAX_TONE_NAME_LENGTH,
  TONES,
  countWords,
  isEmptyStyleRules,
  type CustomTone,
  type CustomToneInput,
  type RewriteOptions,
  type Tone,
} from '@lyrmo/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import type { Messages } from '../../i18n/en';
import { useI18n } from '../../i18n/i18n-context';
import { toneItems, type MenuItem } from '../rewrite/menu-items';
import { useAccount } from './account-context';
import { AccountFrame } from './AccountFrame';
import {
  ConfirmDelete,
  DuplicateBuiltIn,
  ItemHead,
  ItemSection,
  ItemSettings,
  ManagerLayout,
  ManagerList,
  OwnActions,
  TrySample,
} from './ItemManager';
import { useTrials } from './useTrials';
import './tones.css';

const MAX_TERMS = 30;
const MAX_AVOID = 30;
const MAX_PHRASE = 60;
const MAX_NOTES = 500;
const MAX_ALTERNATIVES = 6;

/** The tone as edited: lists of phrases are typed as comma-separated text. */
interface Draft {
  readonly name: string;
  readonly description: string;
  readonly terms: readonly {
    readonly key: number;
    readonly use: string;
    readonly insteadOf: string;
  }[];
  readonly avoid: string;
  readonly notes: string;
}

const splitList = (text: string) =>
  text
    .split(',')
    .map((phrase) => phrase.trim())
    .filter(Boolean);

function toInput(draft: Draft): CustomToneInput {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    rules: {
      // Half-filled rows are dropped rather than refused.
      terms: draft.terms
        .map(({ use, insteadOf }) => ({ use: use.trim(), insteadOf: splitList(insteadOf) }))
        .filter(({ use, insteadOf }) => use && insteadOf.length > 0),
      avoid: splitList(draft.avoid),
      notes: draft.notes.trim(),
    },
  };
}

/** What's wrong with the tone, in the writer's terms, before the server says so. */
function problemWith(
  { name, rules }: CustomToneInput,
  others: readonly CustomTone[],
  t: Messages,
): string | null {
  if (!name) return t.tones.nameMissing;
  const sameName = (other: CustomTone) => other.name.toLowerCase() === name.toLowerCase();
  if (others.some(sameName)) return t.tones.nameTaken(name);
  const phrases = [
    ...rules.terms.flatMap(({ use, insteadOf }) => [use, ...insteadOf]),
    ...rules.avoid,
  ];
  if (phrases.some((phrase) => phrase.length > MAX_PHRASE)) {
    return t.tones.termTooLong(MAX_PHRASE);
  }
  if (rules.terms.some(({ insteadOf }) => insteadOf.length > MAX_ALTERNATIVES)) {
    return t.tones.tooManyAlternatives(MAX_ALTERNATIVES);
  }
  if (rules.avoid.length > MAX_AVOID) return t.tones.tooManyAvoid(MAX_AVOID);
  return null;
}

interface ToneFormProps {
  /** The tone to change, or null to make a new one. */
  readonly tone: CustomTone | null;
  /** What the form starts with: the tone's own, or a duplicate's. */
  readonly initial: CustomToneInput;
  /** The writer's other tones, whose names this one can't take. */
  readonly others: readonly CustomTone[];
  readonly onSaved: (tone: CustomTone) => void;
  readonly onCancel: () => void;
}

function ToneForm({ tone, initial, others, onSaved, onCancel }: ToneFormProps) {
  const { t, describeError } = useI18n();
  const { client } = useAccount();
  const [draft, setDraft] = useState<Draft>(() => ({
    name: initial.name,
    description: initial.description,
    // The saved terms take the first keys; nextKey hands out the rest.
    terms: initial.rules.terms.map(({ use, insteadOf }, key) => ({
      key,
      use,
      insteadOf: insteadOf.join(', '),
    })),
    avoid: initial.rules.avoid.join(', '),
    notes: initial.rules.notes,
  }));
  const nextKey = useRef(draft.terms.length);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = {
    name: useId(),
    description: useId(),
    terms: useId(),
    avoid: useId(),
    notes: useId(),
  };

  const update = (changes: Partial<Draft>) => {
    setDraft({ ...draft, ...changes });
    setError(null);
  };

  async function save(current: Draft) {
    const input = toInput(current);
    const problem = problemWith(input, others, t);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      onSaved(
        tone ? await client.updateCustomTone(tone.id, input) : await client.createCustomTone(input),
      );
    } catch (caught) {
      setError(describeError(caught));
      setSaving(false);
    }
  }

  return (
    <form
      className="tone-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save(draft);
      }}
    >
      <h3 className="item-name">{tone ? t.menus.editItem(tone.name) : t.tones.newTone}</h3>
      <div className="tone-field">
        <label className="field-label" htmlFor={ids.name}>
          {t.tones.name}
        </label>
        <input
          id={ids.name}
          className="text-input"
          required
          maxLength={MAX_TONE_NAME_LENGTH}
          placeholder={t.tones.namePlaceholder}
          value={draft.name}
          onChange={(event) => {
            update({ name: event.target.value });
          }}
        />
      </div>

      <div className="tone-field">
        <label className="field-label" htmlFor={ids.description}>
          {t.tones.description}
        </label>
        <textarea
          id={ids.description}
          className="text-input tone-notes"
          rows={2}
          maxLength={MAX_TONE_DESCRIPTION_LENGTH}
          placeholder={t.tones.descriptionPlaceholder}
          value={draft.description}
          onChange={(event) => {
            update({ description: event.target.value });
          }}
        />
      </div>

      {/* A group rather than a fieldset: browsers lay a legend outside the fieldset's grid, so
          its gap wouldn't match the other fields'. */}
      <div className="tone-field" role="group" aria-labelledby={ids.terms}>
        <p className="field-label" id={ids.terms}>
          {t.tones.terms}
        </p>
        {draft.terms.length > 0 && (
          <ul className="term-list">
            {draft.terms.map((term, index) => (
              <li key={term.key} className="term-row">
                <input
                  className="text-input"
                  aria-label={t.tones.term(index + 1)}
                  placeholder={t.tones.termPlaceholder}
                  maxLength={MAX_PHRASE}
                  value={term.use}
                  onChange={(event) => {
                    update({
                      terms: draft.terms.map((other) =>
                        other.key === term.key ? { ...other, use: event.target.value } : other,
                      ),
                    });
                  }}
                />
                <span className="term-instead">{t.tones.insteadOf}</span>
                <input
                  className="text-input"
                  aria-label={t.tones.replaces(term.use || t.tones.termN(index + 1))}
                  placeholder={t.tones.alternativesPlaceholder}
                  value={term.insteadOf}
                  onChange={(event) => {
                    update({
                      terms: draft.terms.map((other) =>
                        other.key === term.key
                          ? { ...other, insteadOf: event.target.value }
                          : other,
                      ),
                    });
                  }}
                />
                <button
                  type="button"
                  className="button button--ghost icon-button"
                  aria-label={t.tones.remove(term.use || t.tones.termN(index + 1))}
                  title={t.tones.remove(term.use || t.tones.termN(index + 1))}
                  onClick={() => {
                    update({ terms: draft.terms.filter((other) => other.key !== term.key) });
                  }}
                >
                  <Icon name="close" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button
          className="term-add"
          disabled={draft.terms.length >= MAX_TERMS}
          onClick={() => {
            update({
              terms: [...draft.terms, { key: nextKey.current++, use: '', insteadOf: '' }],
            });
          }}
        >
          {t.tones.addTerm}
        </Button>
      </div>

      <div className="tone-field">
        <label className="field-label" htmlFor={ids.avoid}>
          {t.tones.avoid}
        </label>
        <input
          id={ids.avoid}
          className="text-input"
          placeholder={t.tones.avoidPlaceholder}
          value={draft.avoid}
          onChange={(event) => {
            update({ avoid: event.target.value });
          }}
        />
        <p className="field-hint">{t.tones.separate}</p>
      </div>

      <div className="tone-field">
        <label className="field-label" htmlFor={ids.notes}>
          {t.tones.notes}
        </label>
        <textarea
          id={ids.notes}
          className="text-input tone-notes"
          rows={3}
          maxLength={MAX_NOTES}
          placeholder={t.tones.notesPlaceholder}
          value={draft.notes}
          onChange={(event) => {
            update({ notes: event.target.value });
          }}
        />
        <p className="field-hint">
          {draft.notes.length} / {MAX_NOTES}
        </p>
      </div>

      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <Button onClick={onCancel}>{t.common.cancel}</Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t.tones.saving : t.tones.save}
        </Button>
      </div>
    </form>
  );
}

/** A tone's rules, as a list: its preferred terms, the words it avoids, and the rest. */
function ToneRules({ tone }: { readonly tone: CustomTone }) {
  const { t } = useI18n();
  const { terms, avoid, notes } = tone.rules;
  if (isEmptyStyleRules(tone.rules)) return <p className="item-note">{t.tones.noRules}</p>;
  return (
    <dl className="item-rules">
      {terms.length > 0 && (
        <div className="item-rule">
          <dt>{t.tones.terms}</dt>
          <dd>
            <ul className="item-terms">
              {terms.map(({ use, insteadOf }) => (
                <li key={use}>
                  <span className="item-term-use">{use}</span>{' '}
                  <span className="item-term-instead">
                    {t.tones.insteadOf} {insteadOf.join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </dd>
        </div>
      )}
      {avoid.length > 0 && (
        <div className="item-rule">
          <dt>{t.tones.avoid}</dt>
          <dd>
            <ul className="item-chips">
              {avoid.map((phrase) => (
                <li key={phrase} className="item-chip">
                  {phrase}
                </li>
              ))}
            </ul>
          </dd>
        </div>
      )}
      {notes && (
        <div className="item-rule">
          <dt>{t.tones.notes}</dt>
          <dd>{notes}</dd>
        </div>
      )}
    </dl>
  );
}

/** What the right of the dialog shows: a tone, or the form making or changing one. */
type Pane =
  | { readonly kind: 'tone'; readonly id: string }
  | {
      readonly kind: 'form';
      readonly tone: CustomTone | null;
      readonly draft: CustomToneInput;
      /** The tone to show again on Cancel; null when made from the menu, closing instead. */
      readonly back: string | null;
    };

const FIRST_TONE: Tone = TONES[0];
const NEW_TONE: CustomToneInput = { name: '', description: '', rules: EMPTY_STYLE_RULES };

/** How a tone is asked for in a rewrite: a built-in one by name, or the writer's own by id. */
const optionsFor = (id: string): RewriteOptions => {
  const tone = TONES.find((builtIn) => builtIn === id);
  return tone
    ? { tone, length: 'same', language: 'auto' }
    : { tone: 'natural', customTone: id, length: 'same', language: 'auto' };
};

/**
 * Every tone the writer has, the built-in ones and their own: whether the tone menu shows each,
 * whether it's pinned, how it sounds, and a rewrite of a sample in it. Their own tones are
 * changed, duplicated, and deleted here; built-in ones are duplicated to make one of their own.
 */
function ToneManager() {
  const { t, format, describeError } = useI18n();
  const {
    client,
    customTones,
    reloadCustomTones,
    menuSettings,
    reloadMenuSettings,
    keepMenuItem,
    newTone,
    closeDialog,
  } = useAccount();
  // Opened from the tone menu's New tone, it starts with a new one.
  const [pane, setPane] = useState<Pane>(() =>
    newTone
      ? { kind: 'form', tone: null, draft: NEW_TONE, back: null }
      : { kind: 'tone', id: FIRST_TONE },
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trials, tryIt, forget } = useTrials();
  const own = customTones ?? [];
  const settings = menuSettings.tones;
  const items = toneItems(own, settings, t);
  const full = customTones !== null && customTones.length >= MAX_CUSTOM_TONES;

  // Opening the dialog brings the tones up to date, as they can change in another tab.
  useEffect(() => {
    Promise.all([reloadCustomTones(), reloadMenuSettings('tones')]).catch((caught: unknown) => {
      setLoadError(describeError(caught));
    });
  }, [describeError, reloadCustomTones, reloadMenuSettings]);

  const show = (id: string) => {
    setPane({ kind: 'tone', id });
    setSaved(null);
    setDeleting(false);
    setError(null);
  };

  const edit = (tone: CustomTone | null, draft: CustomToneInput, back: string | null) => {
    setPane({ kind: 'form', tone, draft, back });
    setSaved(null);
  };

  const duplicate = (item: MenuItem) => {
    const name = t.menus.copyName(item.name).slice(0, MAX_TONE_NAME_LENGTH);
    const tone = own.find((candidate) => candidate.id === item.id);
    const builtIn = TONES.find((candidate) => candidate === item.id);
    const description = tone?.description ?? (builtIn ? t.tones.about[builtIn] : '');
    edit(
      null,
      {
        name,
        description: description.slice(0, MAX_TONE_DESCRIPTION_LENGTH),
        rules: tone?.rules ?? EMPTY_STYLE_RULES,
      },
      item.id,
    );
  };

  async function remove(tone: CustomTone) {
    setBusy(true);
    setError(null);
    try {
      await client.deleteCustomTone(tone.id);
      await Promise.all([reloadCustomTones(), reloadMenuSettings('tones')]);
      show(FIRST_TONE);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const selected = pane.kind === 'tone' ? pane.id : (pane.tone?.id ?? null);

  const details = () => {
    if (pane.kind === 'form') {
      return (
        <ToneForm
          // A fresh form for each tone, or duplicate, it's opened for.
          key={`${pane.tone?.id ?? 'new'}:${pane.draft.name}`}
          tone={pane.tone}
          initial={pane.draft}
          others={own.filter((other) => other.id !== pane.tone?.id)}
          onSaved={(tone) => {
            // Its try, if any, was in how it sounded before.
            forget(tone.id);
            // Shown once it's listed, so the list and the tone come up together.
            const done = () => {
              show(tone.id);
              setSaved(tone.name);
            };
            reloadCustomTones().then(done, (caught: unknown) => {
              done();
              setError(describeError(caught));
            });
          }}
          onCancel={() => {
            // Made from the menu, it goes back to the draft.
            if (pane.back === null && newTone && !pane.tone) closeDialog();
            else show(pane.tone?.id ?? pane.back ?? FIRST_TONE);
          }}
        />
      );
    }

    // Deleted meanwhile, as in another tab, the first tone takes its place.
    const item = items.find((candidate) => candidate.id === pane.id) ?? items[0];
    if (!item) return null;
    const tone = own.find((candidate) => candidate.id === item.id);
    const builtIn = TONES.find((candidate) => candidate === item.id);
    const credits = countWords(t.menus.sample) * CREDIT_RATES.rewritePerWord;
    return (
      <div className="item-details" key={item.id}>
        {saved && (
          <p className="tones-saved" role="status">
            {t.tones.saved(saved)}
          </p>
        )}
        <ItemHead
          item={item}
          actions={
            tone ? (
              <OwnActions
                name={tone.name}
                full={full}
                busy={busy}
                onEdit={() => {
                  edit(tone, tone, tone.id);
                }}
                onDuplicate={() => {
                  duplicate(item);
                }}
                onDelete={() => {
                  setDeleting(true);
                }}
              />
            ) : (
              <DuplicateBuiltIn
                disabled={full || customTones === null}
                onClick={() => {
                  duplicate(item);
                }}
              />
            )
          }
        />
        {tone && deleting && (
          <ConfirmDelete
            message={t.tones.confirmDelete(tone.name)}
            busy={busy}
            onCancel={() => {
              setDeleting(false);
            }}
            onConfirm={() => void remove(tone)}
          />
        )}
        {full && <p className="field-hint">{t.tones.limit(MAX_CUSTOM_TONES)}</p>}

        <ItemSettings
          item={item}
          ready={settings !== null}
          // As written is what every rewrite starts with, so it stays where it can be chosen.
          alwaysShown={item.id === FIRST_TONE ? t.tones.defaultAlwaysShown : undefined}
          onKeep={(change) => {
            setError(null);
            keepMenuItem('tones', {
              id: item.id,
              hidden: item.hidden,
              pinned: item.pinned,
              ...change,
            }).catch((caught: unknown) => {
              setError(describeError(caught));
            });
          }}
        />
        {error && (
          <p className="dialog-error" role="alert">
            {error}
          </p>
        )}

        <ItemSection title={t.tones.description}>
          {builtIn ? (
            <>
              <p className="item-about">{t.tones.about[builtIn]}</p>
              <p className="item-note">{t.menus.builtInNote}</p>
            </>
          ) : tone?.description ? (
            <p className="item-text">{tone.description}</p>
          ) : (
            <p className="item-note">{t.tones.noDescription}</p>
          )}
        </ItemSection>

        {tone && (
          <ItemSection title={t.tones.rulesTitle}>
            <ToneRules tone={tone} />
          </ItemSection>
        )}

        <TrySample
          trial={trials[item.id]}
          cost={`${format.number(credits)}${t.account.chipUnit(credits)}`}
          onTry={() =>
            void tryIt(item.id, async (signal) => {
              const { rewrites, usage } = await client.createRewrite(t.menus.sample, {
                signal,
                options: optionsFor(item.id),
              });
              return { text: rewrites.optimalVersion, usage };
            })
          }
        />
      </div>
    );
  };

  return (
    <ManagerLayout
      shown={items.filter((item) => !item.hidden).length}
      total={items.length}
      list={
        <ManagerList
          groups={[
            { label: t.menus.builtIn, items: items.filter((item) => item.builtIn) },
            {
              label: t.options.yourTones,
              items: items.filter((item) => !item.builtIn),
              aside: customTones && t.menus.ownCount(customTones.length, MAX_CUSTOM_TONES),
              empty:
                customTones === null ? (
                  loadError ? (
                    <p className="dialog-error" role="alert">
                      {loadError}
                    </p>
                  ) : (
                    <p className="manager-empty" role="status">
                      {t.tones.loading}
                    </p>
                  )
                ) : (
                  <p className="manager-empty">{t.tones.empty}</p>
                ),
            },
          ]}
          selected={selected}
          onSelect={show}
          newLabel={t.tones.newTone}
          newDisabled={customTones === null || full}
          onNew={() => {
            edit(null, NEW_TONE, selected);
          }}
        />
      }
      details={details()}
    />
  );
}

export function TonesDialog() {
  const { t } = useI18n();
  const { account, dialog, closeDialog, openDialog } = useAccount();
  const signedIn = Boolean(account?.user);
  return (
    <AccountFrame
      open={dialog === 'tones'}
      onClose={closeDialog}
      title={t.tones.title}
      wide={signedIn}
    >
      {signedIn ? (
        <ToneManager />
      ) : (
        <>
          <p className="dialog-intro">{t.tones.pitch}</p>
          <div className="form-actions">
            <Button onClick={closeDialog}>{t.common.notNow}</Button>
            <Button
              variant="primary"
              onClick={() => {
                openDialog('sign-in');
              }}
            >
              {t.common.signIn}
            </Button>
          </div>
        </>
      )}
    </AccountFrame>
  );
}
